import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthProvider";
import { adminApi, extractApiError } from "../../lib/adminApi";
import { LogoMark } from "../../components/common/BrandVisuals";
import { KeyRound, ShieldCheck } from "lucide-react";

/**
 * Reusable change-password form card. Renders only the card (no logo, no
 * min-h-screen wrapper). Used embedded in the Compte page and inside the
 * standalone forced-change screen below.
 */
export function ChangePasswordCard({ compact = false }) {
    const { status, reload } = useAdminAuth();
    const nav = useNavigate();
    const forced = status === "must_change_password";
    const [form, setForm] = useState({
        current_password: "",
        new_password: "",
        confirm: "",
    });
    const [state, setState] = useState({ status: "idle", error: "" });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (form.new_password.length < 10) {
            return setState({
                status: "error",
                error: "Le nouveau mot de passe doit contenir au moins 10 caractères.",
            });
        }
        if (form.new_password !== form.confirm) {
            return setState({
                status: "error",
                error: "La confirmation ne correspond pas.",
            });
        }
        setState({ status: "submitting", error: "" });
        try {
            await adminApi.post("/admin/change-password", {
                current_password: form.current_password,
                new_password: form.new_password,
            });
            await reload();
            setForm({ current_password: "", new_password: "", confirm: "" });
            setState({ status: "success", error: "" });
            if (forced) {
                setTimeout(() => nav("/admin", { replace: true }), 900);
            } else {
                setTimeout(
                    () =>
                        setState((s) =>
                            s.status === "success"
                                ? { status: "idle", error: "" }
                                : s,
                        ),
                    2500,
                );
            }
        } catch (err) {
            setState({
                status: "error",
                error: extractApiError(
                    err,
                    "Impossible de changer le mot de passe.",
                ),
            });
        }
    };

    return (
        <div className="card-line p-8" data-testid="admin-change-password-card">
            <div className="flex items-center gap-2 eyebrow mb-2">
                {forced ? (
                    <>
                        <ShieldCheck size={14} /> Changement obligatoire
                    </>
                ) : (
                    <>
                        <KeyRound size={14} /> Sécurité
                    </>
                )}
            </div>
            <h2
                className={`font-serif text-neutral-50 mb-3 ${
                    compact ? "text-xl" : "text-2xl"
                }`}
            >
                {forced
                    ? "Créez un nouveau mot de passe"
                    : "Changer le mot de passe"}
            </h2>
            {forced && (
                <p className="text-sm text-neutral-400 mb-6 leading-relaxed">
                    Pour des raisons de sécurité, le mot de passe temporaire
                    doit être remplacé avant d'accéder à l'administration.
                </p>
            )}
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <label className="block">
                    <span className="eyebrow block mb-1">
                        Mot de passe actuel
                    </span>
                    <input
                        type="password"
                        required
                        className="field-box"
                        value={form.current_password}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                current_password: e.target.value,
                            })
                        }
                        data-testid="admin-change-current"
                    />
                </label>
                <label className="block">
                    <span className="eyebrow block mb-1">
                        Nouveau mot de passe (min. 10 caractères)
                    </span>
                    <input
                        type="password"
                        required
                        minLength={10}
                        className="field-box"
                        value={form.new_password}
                        onChange={(e) =>
                            setForm({ ...form, new_password: e.target.value })
                        }
                        data-testid="admin-change-new"
                    />
                </label>
                <label className="block">
                    <span className="eyebrow block mb-1">Confirmation</span>
                    <input
                        type="password"
                        required
                        className="field-box"
                        value={form.confirm}
                        onChange={(e) =>
                            setForm({ ...form, confirm: e.target.value })
                        }
                        data-testid="admin-change-confirm"
                    />
                </label>
                {state.status === "error" && (
                    <div
                        className="text-sm text-red-300 border border-red-900 bg-red-950/40 px-3 py-2"
                        data-testid="admin-change-error"
                    >
                        {state.error}
                    </div>
                )}
                {state.status === "success" && (
                    <div
                        className="text-sm text-emerald-300 border border-emerald-900 bg-emerald-950/40 px-3 py-2"
                        data-testid="admin-change-success"
                    >
                        Mot de passe modifié avec succès.
                    </div>
                )}
                <button
                    type="submit"
                    disabled={state.status === "submitting"}
                    className="btn-gold w-full justify-center"
                    data-testid="admin-change-submit"
                >
                    Valider le nouveau mot de passe
                </button>
            </form>
        </div>
    );
}

/**
 * Standalone forced-change screen — full viewport, own MABS logo. Used ONLY
 * by the /admin/change-password route (when the admin is locked out).
 */
export default function AdminChangePassword() {
    const { status } = useAdminAuth();
    if (status === "unauthenticated") {
        return <Navigate to="/admin/login" replace />;
    }
    return (
        <div
            data-testid="admin-change-password-page"
            className="min-h-screen bg-ink flex items-center justify-center px-5 py-10"
        >
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-6">
                    <LogoMark size="md" />
                </div>
                <ChangePasswordCard />
            </div>
        </div>
    );
}
